import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store';
import { updateTrackInList } from '../store/tracksSlice';
import {
  ArrowLeft,
  Play,
  Pause,
  RotateCcw,
  Video,
  Settings,
  Heart,
  Layers,
  Save,
  Lock,
  Eye,
  Edit2,
  ChevronDown,
} from 'lucide-react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import maplibreglWorkerUrl from 'maplibre-gl/dist/maplibre-gl-csp-worker.js?url';

maplibregl.setWorkerUrl(maplibreglWorkerUrl);
import { CameraAnimator, PROFILES } from '../utils/camera-animator';
import { getProvider } from '../utils/map-providers';
import type { TileConfig } from '../utils/map-providers';
import { apiGet, apiPost, apiPatch, resolveTileUrl } from '../utils/api';
import { formatDistance, formatElevation, formatSpeedOrPace } from '../utils/formatters';
import { proposeDuration } from '../utils/propose-duration';
import { useUI } from '../context/UIContext';
import { trackGAEvent } from '../utils/analytics';

export const PreviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const token = useAppSelector((state) => state.auth.token);
  const user = useAppSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const ui = useUI();

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const isRenderMode = searchParams.get('render') === 'true';
  const initialAspectRatio = (searchParams.get('aspectRatio') || '16:9') as '16:9' | '4:3' | '1:1' | '9:16';

  const [track, setTrack] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limits, setLimits] = useState<Record<string, number>>({ '720p': 5, '1080p': 1, '4K': 0 });
  const [userPlan, setUserPlan] = useState<string>('free');

  // Animation Controls
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1); // 0.5x, 1x, 2x, 4x
  const [cameraProfileName, setCameraProfileName] = useState<string>('running');

  // Render Job Config
  const [resolution, setResolution] = useState<'720p' | '1080p' | '4K'>('1080p');
  const [fps, setFps] = useState<30 | 60>(30);
  const [customDuration, setCustomDuration] = useState<number>(30);
  const [suggestedDuration, setSuggestedDuration] = useState<number>(30);

  const [aspectRatio, setAspectRatio] = useState<'16:9' | '4:3' | '1:1' | '9:16'>(initialAspectRatio);
  const [renderingQueue, setRenderingQueue] = useState(false);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [tileConfig, setTileConfig] = useState<TileConfig | null>(null);
  const [introDuration, setIntroDuration] = useState<number>(2);
  const [outroDuration, setOutroDuration] = useState<number>(3);
  const [terrainExaggeration, setTerrainExaggeration] = useState<number>(1.5);

  // Map Appearance Settings
  const [mapProvider, setMapProvider] = useState<string>('');
  const [hillshade, setHillshade] = useState<boolean>(false);

  // HUD Visibility Settings
  const [hudSettings, setHudSettings] = useState<Record<string, boolean>>({
    showOverlay: true,
    showDistance: true,
    showElevation: true,
    showDuration: true,
    showPace: true,
    showHeartRate: true,
    showElevationProfile: true,
    showCourseMiniature: true,
    hideOverlayOnIntroOutro: false,
  });

  // Saving indicator
  const [savingDefault, setSavingDefault] = useState(false);

  // Renaming State
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');

  // Accordion Sections State
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    timeline: true,
    map: false,
    hud: false,
    quality: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const startEditing = () => {
    if (!track) return;
    setEditingName(track.name);
    setIsEditingName(true);
  };

  const saveName = async () => {
    if (!track || !editingName.trim()) return;
    try {
      const updatedTrack = await apiPatch(`/api/tracks/${track.id}`, { name: editingName.trim() });
      setTrack(updatedTrack);
      dispatch(updateTrackInList(updatedTrack));
      setIsEditingName(false);
    } catch (err: any) {
      console.error('Failed to rename track:', err);
      ui.alert(err.message || 'Failed to rename track', 'Rename Failed');
    }
  };

  // Current Telemetry Stats Overlay
  const [stats, setStats] = useState({
    distance: 0,
    elevation: 0,
    elapsedTime: '00:00:00',
    speed: 0,
    heartRate: 0 as number | undefined,
  });

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const animatorRef = useRef<CameraAnimator | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const durationRef = useRef<number>(0);

  const lastZoomRef = useRef<number | null>(null);
  const lastProgressRef = useRef<number | null>(null);
  // Store the highest terrain elevation we've measured to act as a floor
  const maxMeasuredTerrainRef = useRef<number>(0);
  // Track last jumpTo values to skip redundant calls that prevent tile loading
  const lastJumpRef = useRef<{ center: [number, number]; zoom: number; pitch: number; bearing: number } | null>(null);
  // Track last animation phase to skip redundant GeoJSON source updates during static intro/outro
  const lastPhaseRef = useRef<'INTRO' | 'OUTRO' | 'ACTIVE' | null>(null);

  /**
   * Two-phase camera terrain avoidance:
   * Phase 1: jumpTo keyframe position (tiles load from previous frame)
   * Phase 2: query terrain at camera position, correct with second jumpTo if needed
   */
  const safeJumpTo = useCallback((map: maplibregl.Map | null, options: any, currentProgress?: number) => {
    if (!map) return;

    try {
      // Detect progress jump (user seeking) - reset elevation memory
      if (currentProgress !== undefined) {
        if (lastProgressRef.current !== null && Math.abs(currentProgress - lastProgressRef.current) > 0.05) {
          lastZoomRef.current = null;
          maxMeasuredTerrainRef.current = 0;
          lastJumpRef.current = null; // Reset on seek so next frame always applies
          lastPhaseRef.current = null; // Reset phase on seek so next frame updates map sources
        }
        lastProgressRef.current = currentProgress;
      }

      let targetZoom = options.zoom;

      // Query terrain at camera position and compute safe zoom if currentProgress is provided
      if (currentProgress !== undefined) {
        const pitch = options.pitch || 0;
        const bearing = options.bearing || 0;
        const center: [number, number] = options.center;
        const baseZoom: number = options.zoom;

        const pitchRad = (pitch * Math.PI) / 180;
        const distance = 37500000 / Math.pow(2, baseZoom);
        const altitudeAboveCenter = distance * Math.cos(pitchRad);
        const horizontalDistance = distance * Math.sin(pitchRad);

        // Query terrain elevation at center
        const centerElevation = map.queryTerrainElevation(center) ?? 0;

        // Calculate camera's lat/lng position (behind center along bearing)
        const alpha = ((bearing + 180) * Math.PI) / 180;
        const dx = horizontalDistance * Math.sin(alpha);
        const dy = horizontalDistance * Math.cos(alpha);
        const cameraLat = center[1] + dy / 111111;
        const cameraLng = center[0] + dx / (111111 * Math.cos((center[1] * Math.PI) / 180));

        // Sample terrain along the ray from center to camera
        const NUM_SAMPLES = 5;
        let maxTerrainEle = centerElevation;

        for (let i = 1; i <= NUM_SAMPLES; i++) {
          const t = i / NUM_SAMPLES;
          const sampleLng = center[0] + (cameraLng - center[0]) * t;
          const sampleLat = center[1] + (cameraLat - center[1]) * t;
          const ele = map.queryTerrainElevation([sampleLng, sampleLat]);
          if (ele !== null && ele > maxTerrainEle) {
            maxTerrainEle = ele;
          }
        }

        // Sample points to the left and right of the camera
        const perpAlpha = alpha + Math.PI / 2;
        const sideOffset = horizontalDistance * 0.3;
        for (const sign of [-1, 1]) {
          const sideDx = sideOffset * Math.sin(perpAlpha) * sign;
          const sideDy = sideOffset * Math.cos(perpAlpha) * sign;
          const sideLng = cameraLng + sideDx / (111111 * Math.cos((cameraLat * Math.PI) / 180));
          const sideLat = cameraLat + sideDy / 111111;
          const ele = map.queryTerrainElevation([sideLng, sideLat]);
          if (ele !== null && ele > maxTerrainEle) {
            maxTerrainEle = ele;
          }
        }

        // Track highest terrain elevation (decay slowly)
        if (maxTerrainEle > maxMeasuredTerrainRef.current) {
          maxMeasuredTerrainRef.current = maxTerrainEle;
        } else {
          maxMeasuredTerrainRef.current = maxMeasuredTerrainRef.current * 0.97 + maxTerrainEle * 0.03;
        }

        const effectiveTerrainEle = Math.max(maxTerrainEle, maxMeasuredTerrainRef.current);
        const cameraAltitude = centerElevation + altitudeAboveCenter;
        const safetyMargin = 100; // meters above terrain

        if (cameraAltitude < effectiveTerrainEle + safetyMargin) {
          const requiredAltAboveCenter = effectiveTerrainEle + safetyMargin - centerElevation;
          const requiredDistance = requiredAltAboveCenter / Math.cos(pitchRad);
          const correctedZoom = Math.log2(37500000 / requiredDistance);
          const safeZoom = Math.min(baseZoom, correctedZoom);

          let finalZoom = safeZoom;
          const lastZoom = lastZoomRef.current;
          if (lastZoom !== null && safeZoom < lastZoom) {
            finalZoom = lastZoom + (safeZoom - lastZoom) * 0.7;
          }
          lastZoomRef.current = finalZoom;
          targetZoom = finalZoom;
        } else {
          // No terrain collision — use keyframe baseZoom directly without lagging/throttling
          lastZoomRef.current = null;
          targetZoom = baseZoom;
        }
      }

      // Skip redundant jumpTo calls when camera hasn't moved meaningfully
      const last = lastJumpRef.current;
      if (last) {
        const dCenter = Math.abs(options.center[0] - last.center[0]) + Math.abs(options.center[1] - last.center[1]);
        const dZoom = Math.abs(targetZoom - last.zoom);
        const dPitch = Math.abs((options.pitch || 0) - last.pitch);
        const dBearing = Math.abs((options.bearing || 0) - last.bearing);
        if (dCenter < 0.00001 && dZoom < 0.001 && dPitch < 0.05 && dBearing < 0.05) {
          return;
        }
      }

      // Single jumpTo call per frame with final calculated options
      map.jumpTo({
        ...options,
        zoom: targetZoom,
      });

      lastJumpRef.current = {
        center: options.center,
        zoom: targetZoom,
        pitch: options.pitch || 0,
        bearing: options.bearing || 0,
      };
    } catch (e) {
      console.warn('safeJumpTo error:', e);
    }
  }, []);

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const getMapContainerDimensions = useCallback(() => {
    if (isRenderMode) {
      return {
        width: typeof window !== 'undefined' ? window.innerWidth : 1920,
        height: typeof window !== 'undefined' ? window.innerHeight : 1080,
      };
    }

    if (dimensions.width === 0 || dimensions.height === 0) {
      return {
        width: typeof window !== 'undefined' ? window.innerWidth : 1920,
        height: typeof window !== 'undefined' ? window.innerHeight : 1080,
      };
    }

    let r = 16 / 9;
    if (aspectRatio === '4:3') r = 4 / 3;
    else if (aspectRatio === '1:1') r = 1;
    else if (aspectRatio === '9:16') r = 9 / 16;

    const parentW = dimensions.width;
    const parentH = dimensions.height;

    let w = parentW;
    let h = parentH;

    if (parentW / parentH > r) {
      // Parent is wider than target aspect ratio -> height constrained
      h = parentH;
      w = parentH * r;
    } else {
      // Parent is taller than target aspect ratio -> width constrained
      w = parentW;
      h = parentW / r;
    }

    return { width: Math.round(w), height: Math.round(h) };
  }, [isRenderMode, dimensions, aspectRatio]);

  // Callback ref to dynamically instantiate/cleanup ResizeObserver
  const previewAreaCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      if (isRenderMode) return;

      if (node) {
        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            const { width, height } = entry.contentRect;
            setDimensions({ width, height });
          }
        });
        resizeObserver.observe(node);
        resizeObserverRef.current = resizeObserver;
      }
    },
    [isRenderMode],
  );

  // 1. Fetch Track & Tile Config once on mount / id change
  useEffect(() => {
    if (!id || !token) return;

    let active = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch track details
        const trackData = await apiGet(`/api/tracks/${id}`);

        // Fetch Tile config (includes terrain encoding and provider list)
        const tileConfigData = await apiGet('/api/tiles/config');

        // Fetch user plan limits to check locked resolutions dynamically
        let plan = 'free';
        try {
          const profileData = await apiGet('/api/auth/profile');
          if (profileData?.quota?.limits) {
            setLimits(profileData.quota.limits);
          }
          if (profileData?.user?.plan) {
            plan = profileData.user.plan;
            setUserPlan(plan);
          }
        } catch (err) {
          console.error('Failed to load user plan limits:', err);
        }

        if (!active) return;
        durationRef.current = trackData.totalDuration;
        setTrack(trackData);
        setTileConfig(tileConfigData);
        setMapProvider(tileConfigData.defaultProviderId);
        setCameraProfileName(trackData.activityType);

        const suggested = proposeDuration(trackData.totalDistance);
        setSuggestedDuration(suggested);

        // Load per-track visualization settings from the track response (visualSettings field)
        const vs = trackData.visualSettings;
        if (vs) {
          const qProfile = searchParams.get('cameraProfileName');
          const qRes = searchParams.get('resolution');
          const qFps = searchParams.get('fps');
          const qAspect = searchParams.get('aspectRatio');

          if (qProfile) setCameraProfileName(qProfile);
          else if (vs.cameraProfileName) setCameraProfileName(vs.cameraProfileName);

          if (qRes === '720p' || qRes === '1080p' || qRes === '4K') setResolution(qRes);
          else if (vs.resolution) setResolution(vs.resolution as any);

          let resolvedFps = 30;
          if (qFps === '30' || qFps === '60') {
            resolvedFps = Number(qFps);
          } else if (vs.fps) {
            resolvedFps = vs.fps;
          }
          if (resolvedFps === 60 && !['pro', 'expert', 'ultra'].includes(plan)) {
            resolvedFps = 30;
          }
          setFps(resolvedFps as 30 | 60);

          if (vs.customDuration) {
            setCustomDuration(vs.customDuration);
          } else {
            setCustomDuration(suggested);
          }

          if (qAspect === '16:9' || qAspect === '4:3' || qAspect === '1:1' || qAspect === '9:16')
            setAspectRatio(qAspect);
          else if (vs.aspectRatio) setAspectRatio(vs.aspectRatio as any);

          if (vs.mapProvider) setMapProvider(vs.mapProvider);
          if (vs.hillshade !== undefined) setHillshade(vs.hillshade);
          if (vs.introDuration !== undefined) setIntroDuration(vs.introDuration);
          if (vs.outroDuration !== undefined) setOutroDuration(vs.outroDuration);
          if (vs.terrainExaggeration !== undefined) setTerrainExaggeration(vs.terrainExaggeration);
          if (vs.hudSettings)
            setHudSettings({
              showOverlay: vs.hudSettings.showOverlay !== undefined ? vs.hudSettings.showOverlay : true,
              showDistance: vs.hudSettings.showDistance !== undefined ? vs.hudSettings.showDistance : true,
              showElevation: vs.hudSettings.showElevation !== undefined ? vs.hudSettings.showElevation : true,
              showDuration: vs.hudSettings.showDuration !== undefined ? vs.hudSettings.showDuration : true,
              showPace: vs.hudSettings.showPace !== undefined ? vs.hudSettings.showPace : true,
              showHeartRate: vs.hudSettings.showHeartRate !== undefined ? vs.hudSettings.showHeartRate : true,
              showElevationProfile:
                vs.hudSettings.showElevationProfile !== undefined ? vs.hudSettings.showElevationProfile : true,
              showCourseMiniature:
                vs.hudSettings.showCourseMiniature !== undefined ? vs.hudSettings.showCourseMiniature : true,
              hideOverlayOnIntroOutro:
                vs.hudSettings.hideOverlayOnIntroOutro !== undefined ? vs.hudSettings.hideOverlayOnIntroOutro : false,
            });
        } else {
          setCustomDuration(suggested);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to initialize preview');
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [id, token, searchParams]);

  // 1.5. Initialize/Re-create Map when track, tileConfig, mapProvider, or hillshade changes
  useEffect(() => {
    if (!track || !tileConfig) return;

    let active = true;
    setIsMapLoaded(false);

    const providerCfg = getProvider(tileConfig, mapProvider);
    const profile = PROFILES[track.activityType] || PROFILES.other;

    const mapSources: any = {
      'map-tiles': {
        type: 'raster',
        tiles: [resolveTileUrl(providerCfg.tileUrl)],
        tileSize: providerCfg.tileSize,
        maxzoom: providerCfg.maxzoom,
        attribution: providerCfg.attribution,
      },
      'terrain-dem': {
        type: 'raster-dem',
        tiles: [resolveTileUrl(tileConfig.terrain.tileUrl)],
        tileSize: tileConfig.terrain.encoding === 'mapbox' ? 512 : 256,
        maxzoom: Math.min(14, tileConfig.terrain.maxZoom),
        encoding: tileConfig.terrain.encoding,
      },
    };

    const mapLayers: any[] = [
      {
        id: 'map-tiles-layer',
        type: 'raster',
        source: 'map-tiles',
        paint: {
          'raster-fade-duration': 300,
          'raster-resampling': 'linear',
        },
      },
    ];

    if (hillshade) {
      mapLayers.push({
        id: 'hillshade-layer',
        type: 'hillshade',
        source: 'terrain-dem',
        paint: {
          'hillshade-exaggeration': 0.5,
          'hillshade-shadow-color': '#2a3f5f',
          'hillshade-highlight-color': '#ffffff',
          'hillshade-accent-color': '#3d5a80',
          'hillshade-illumination-direction': 315,
        },
      });
    }

    if (!mapContainerRef.current) {
      console.warn('Map container is not ready in DOM');
      return;
    }

    // Compute track center for initial map position
    const trackCoords = track.telemetry.points;
    let tMinLon = Infinity,
      tMaxLon = -Infinity,
      tMinLat = Infinity,
      tMaxLat = -Infinity;
    for (const p of trackCoords) {
      if (p.lon < tMinLon) tMinLon = p.lon;
      if (p.lon > tMaxLon) tMaxLon = p.lon;
      if (p.lat < tMinLat) tMinLat = p.lat;
      if (p.lat > tMaxLat) tMaxLat = p.lat;
    }
    const initCenter: [number, number] = [(tMinLon + tMaxLon) / 2, (tMinLat + tMaxLat) / 2];

    (maplibregl as any).maxParallelImageRequests = 32;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      center: initCenter,
      zoom: profile.introZoom,
      pitch: 1,
      fadeDuration: 300,
      preserveDrawingBuffer: true,
      maxTileCacheSize: 1500,
      maxTileCacheZoomLevels: 24,
      maxPitch: 85,
      style: {
        version: 8,
        sources: mapSources,
        layers: mapLayers,
        terrain: {
          source: 'terrain-dem',
          exaggeration: terrainExaggeration,
        },
        sky: {
          'sky-color': '#0a0d14',
          'horizon-color': '#1a2234',
          'horizon-fog-blend': 0.8,
        },
      },
    } as any);

    mapRef.current = map;

    map.on('error', (e) => {
      console.error('[MapErrorEvent]', e);
    });
    map.on('sourcedata', (e) => {
      if (e.sourceId === 'cursor') {
        console.log(`[SourceDataEvent][cursor] dataType=${e.dataType} isSourceLoaded=${map.isSourceLoaded('cursor')} | coord=${map.getSource('cursor') ? JSON.stringify((map.getSource('cursor') as any)._data?.geometry?.coordinates) : 'none'}`);
      }
    });

    map.on('load', () => {
      if (!active) return;

      const coordinates = track.telemetry.points.map((p: any) => [p.lon, p.lat]);

      // Initialize Animator first so it is available for geometry initialization
      const { width: mapW, height: mapH } = getMapContainerDimensions();
      const introRatio = introDuration / customDuration;
      const outroRatio = outroDuration / customDuration;
      const profileWithExaggeration = { ...profile, terrainExaggeration };
      const animator = new CameraAnimator(
        track.telemetry.points,
        profileWithExaggeration,
        mapW,
        mapH,
        introRatio,
        outroRatio,
      );
      animatorRef.current = animator;

      // Draw passed track line
      map.addSource('track-passed', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [],
          },
        },
        maxzoom: 24,
        tolerance: 0,
        buffer: 512,
      });

      map.addLayer({
        id: 'track-layer-passed',
        type: 'line',
        source: 'track-passed',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#ff385c',
          'line-width': 6,
          'line-opacity': 0.85,
        },
      });

      // Draw remaining track line
      map.addSource('track-remaining', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates,
          },
        },
        maxzoom: 24,
        tolerance: 0,
        buffer: 512,
      });

      map.addLayer({
        id: 'track-layer-remaining',
        type: 'line',
        source: 'track-remaining',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#4a121b',
          'line-width': 6,
          'line-opacity': 0.6,
        },
      });

      // Initialize geometries for progress
      const initialTelemetry = animator.getTelemetryAtProgress(progress);
      const passedSource = map.getSource('track-passed') as maplibregl.GeoJSONSource;
      const remainingSource = map.getSource('track-remaining') as maplibregl.GeoJSONSource;
      if (passedSource && remainingSource) {
        const targetDist = initialTelemetry.distance || 0;
        const passedCoords: [number, number][] = [];
        const remainingCoords: [number, number][] = [];

        for (const pt of track.telemetry.points) {
          if ((pt.distance || 0) <= targetDist) {
            passedCoords.push([pt.lon, pt.lat]);
          } else {
            remainingCoords.push([pt.lon, pt.lat]);
          }
        }
        passedCoords.push([initialTelemetry.lon, initialTelemetry.lat]);
        remainingCoords.unshift([initialTelemetry.lon, initialTelemetry.lat]);

        passedSource.setData({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: passedCoords,
          },
          properties: {},
        });

        remainingSource.setData({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: remainingCoords,
          },
          properties: {},
        });
      }

      // Draw animated cursor point
      map.addSource('cursor', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: coordinates[0],
          },
          properties: {},
        },
        maxzoom: 14,
        tolerance: 0,
        buffer: 512,
      });

      map.addLayer({
        id: 'cursor-layer',
        type: 'circle',
        source: 'cursor',
        paint: {
          'circle-radius': 8,
          'circle-color': '#ffffff',
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ff385c',
        },
      });

      mapRef.current = map;

      // Set initial camera view (use current progress)
      const initialFrame = animator.getKeyframeAtProgress(progress);
      safeJumpTo(
        map,
        {
          center: initialFrame.center,
          zoom: initialFrame.zoom,
          pitch: initialFrame.pitch,
          bearing: initialFrame.bearing,
        },
        progress,
      );

      updateStats(progress, animator);
      setIsMapLoaded(true);
    });

    return () => {
      active = false;
      setIsMapLoaded(false);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track, tileConfig, mapProvider, hillshade]);

  // 1.6. Update terrain exaggeration on map in real-time
  useEffect(() => {
    if (mapRef.current && isMapLoaded) {
      try {
        mapRef.current.setTerrain({
          source: 'terrain-dem',
          exaggeration: terrainExaggeration,
        });
      } catch (err) {
        console.warn('Failed to update terrain exaggeration on map:', err);
      }
    }
  }, [terrainExaggeration, isMapLoaded]);

  // 2. Handle Camera Profile Changes
  useEffect(() => {
    if (!track || !mapRef.current || !isMapLoaded) return;
    const profile = PROFILES[cameraProfileName] || PROFILES.other;

    // Resize map to fit the current layout (especially aspect ratio changes)
    if (mapRef.current) {
      mapRef.current.resize();
    }

    // Update terrain exaggeration
    if (mapRef.current) {
      try {
        if (mapRef.current.isStyleLoaded()) {
          mapRef.current.setTerrain({
            source: 'terrain-dem',
            exaggeration: terrainExaggeration,
          });
        }
      } catch (err) {
        console.warn('Failed to update terrain exaggeration in camera profile change:', err);
      }
    }

    // Reinitialize Animator with new profile, ratios, and current calculated dimensions
    const { width: mapW, height: mapH } = getMapContainerDimensions();
    const introRatio = introDuration / customDuration;
    const outroRatio = outroDuration / customDuration;
    const profileWithExaggeration = { ...profile, terrainExaggeration };
    const animator = new CameraAnimator(
      track.telemetry.points,
      profileWithExaggeration,
      mapW,
      mapH,
      introRatio,
      outroRatio,
    );
    animatorRef.current = animator;

    // Reposition camera
    const frame = animator.getKeyframeAtProgress(progress);
    safeJumpTo(
      mapRef.current,
      {
        center: frame.center,
        zoom: frame.zoom,
        pitch: frame.pitch,
        bearing: frame.bearing,
      },
      progress,
    );
  }, [
    cameraProfileName,
    terrainExaggeration,
    track,
    dimensions,
    aspectRatio,
    introDuration,
    outroDuration,
    customDuration,
    isMapLoaded,
    getMapContainerDimensions,
    progress,
    safeJumpTo,
  ]);

  // 3. Animation loop using requestAnimationFrame
  const animate = (timestamp: number) => {
    if (!lastTimeRef.current) lastTimeRef.current = timestamp;
    const elapsed = timestamp - lastTimeRef.current;
    lastTimeRef.current = timestamp;

    const duration = customDuration * 1000;
    const delta = (elapsed / duration) * playbackSpeed;

    let reachedEnd = false;
    setProgress((prev) => {
      let next = prev + delta;
      if (next >= 1) {
        next = 1;
        reachedEnd = true;
      }

      // Update camera and cursor marker
      updateFrame(next);
      return next;
    });

    if (reachedEnd) {
      setIsPlaying(false);
    } else {
      animationFrameRef.current = requestAnimationFrame(animate);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isPlaying) {
      lastTimeRef.current = 0;
      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, playbackSpeed]);

  // Toggle map interaction constraints based on play/pause state
  useEffect(() => {
    const map = mapRef.current;
    const animator = animatorRef.current;
    if (!map || !animator || isRenderMode) return;

    if (isPlaying) {
      // Remove constraints during animation playback
      map.setMinZoom(0);
      map.setMaxBounds(undefined as any);
    } else {
      // Set minZoom for manual interaction but NEVER set tight maxBounds that clip viewport tiles
      const constraints = animator.getMapConstraints();
      map.setMinZoom(constraints.minZoom);
      map.setMaxBounds(undefined as any);
    }
  }, [isPlaying, isRenderMode, isMapLoaded, dimensions]);

  const updateFrame = (pct: number) => {
    const map = mapRef.current;
    const animator = animatorRef.current;
    if (!map || !animator) return;

    const isIntro = pct < (animator as any).introRatio;
    const isOutro = pct > 1 - (animator as any).outroRatio;
    const phase: 'INTRO' | 'OUTRO' | 'ACTIVE' = isIntro ? 'INTRO' : isOutro ? 'OUTRO' : 'ACTIVE';

    const phaseChanged = lastPhaseRef.current !== phase;
    lastPhaseRef.current = phase;

    // Force progress to 0 or 1.0 when entering INTRO or OUTRO to guarantee exact start/end geometries
    const effectivePct = (phase === 'INTRO' && phaseChanged) ? 0 : (phase === 'OUTRO' && phaseChanged) ? 1 : pct;
    const frame = animator.getKeyframeAtProgress(pct);
    const telemetry = animator.getTelemetryAtProgress(effectivePct);

    if (isIntro || isOutro || pct < 0.1) {
      console.log(`[PreviewDebug][${phase}] pct: ${pct.toFixed(4)} | Target KF: center=[${frame.center[0].toFixed(5)}, ${frame.center[1].toFixed(5)}], zoom=${frame.zoom.toFixed(2)}, pitch=${frame.pitch.toFixed(1)}, bearing=${frame.bearing.toFixed(1)} | Marker: lon=${telemetry.lon.toFixed(5)}, lat=${telemetry.lat.toFixed(5)}`);
    }

    safeJumpTo(
      map,
      {
        center: frame.center,
        zoom: frame.zoom,
        pitch: frame.pitch,
        bearing: frame.bearing,
      },
      pct,
    );

    // Only update cursor and track sources if phase is ACTIVE or if phase changed (e.g. entering INTRO/OUTRO)
    if (phase === 'ACTIVE' || phaseChanged) {
      // Update cursor marker
      const cursorSource = map.getSource('cursor') as maplibregl.GeoJSONSource;
      if (cursorSource) {
        cursorSource.setData({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [telemetry.lon, telemetry.lat],
          },
          properties: {},
        });
        if (isIntro || isOutro || pct < 0.1) {
          console.log(`[PreviewDebug][${phase}] cursorSource.setData updated to [${telemetry.lon.toFixed(5)}, ${telemetry.lat.toFixed(5)}] | Map state after jumpTo: center=[${map.getCenter().lng.toFixed(5)}, ${map.getCenter().lat.toFixed(5)}], zoom=${map.getZoom().toFixed(2)}, pitch=${map.getPitch().toFixed(1)}, loaded=${map.loaded()}, tilesLoaded=${map.areTilesLoaded()}`);
          
          // Projection diagnostic
          const markerPixel = map.project([telemetry.lon, telemetry.lat]);
          const mapCanvas = map.getCanvas();
          const mapWidth = mapCanvas?.clientWidth || 0;
          const mapHeight = mapCanvas?.clientHeight || 0;
          const isPixelOnScreen = markerPixel.x >= 0 && markerPixel.x <= mapWidth && markerPixel.y >= 0 && markerPixel.y <= mapHeight;
          
          let featureFound = false;
          try {
            const features = map.queryRenderedFeatures([markerPixel.x, markerPixel.y], { layers: ['cursor-layer'] });
            featureFound = features.length > 0;
          } catch (e) {}

          console.log(`[MarkerDiagnostic][${phase}] pct: ${pct.toFixed(4)} | Pixel: x=${markerPixel.x.toFixed(1)}, y=${markerPixel.y.toFixed(1)} | OnScreen: ${isPixelOnScreen} | FeatureFound: ${featureFound} | CanvasSize: ${mapWidth}x${mapHeight}`);
        }
      }

      // Update track passed/remaining geometries
      const passedSource = map.getSource('track-passed') as maplibregl.GeoJSONSource;
      const remainingSource = map.getSource('track-remaining') as maplibregl.GeoJSONSource;
      if (passedSource && remainingSource && track?.telemetry?.points) {
        const targetDist = telemetry.distance || 0;
        const passedCoords: [number, number][] = [];
        const remainingCoords: [number, number][] = [];

        for (const pt of track.telemetry.points) {
          if ((pt.distance || 0) <= targetDist) {
            passedCoords.push([pt.lon, pt.lat]);
          } else {
            remainingCoords.push([pt.lon, pt.lat]);
          }
        }

        passedCoords.push([telemetry.lon, telemetry.lat]);
        remainingCoords.unshift([telemetry.lon, telemetry.lat]);

        passedSource.setData({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: passedCoords,
          },
          properties: {},
        });

        remainingSource.setData({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: remainingCoords,
          },
          properties: {},
        });
      }
    }

    updateStats(pct, animator);
  };

  const updateStats = (pct: number, animator: CameraAnimator) => {
    const pt = animator.getTelemetryAtProgress(pct);

    // Format elapsed time
    const totalSeconds = pct * durationRef.current;
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;

    setStats({
      distance: pt.distance ? pt.distance / 1000 : 0,
      elevation: pt.ele,
      elapsedTime: timeStr,
      speed: pt.speed || 0,
      heartRate: pt.hr,
    });

    // Draw overlays on a separate tick to ensure DOM is ready
    requestAnimationFrame(() => {
      const epCanvas = document.getElementById('hud-elevation-profile') as HTMLCanvasElement;
      const cmCanvas = document.getElementById('hud-course-miniature') as HTMLCanvasElement;
      if ((epCanvas || cmCanvas) && track?.telemetry?.points) {
        const totalDistance = track.telemetry.points[track.telemetry.points.length - 1].distance || 1;
        const syncedPct = (pt.distance || 0) / totalDistance;
        if (epCanvas) {
          drawElevationProfile(epCanvas, track.telemetry.points, syncedPct);
        }
        if (cmCanvas) {
          drawCourseMiniature(cmCanvas, track.telemetry.points, syncedPct);
        }
      }
    });
  };

  /** Save per-track settings via PATCH /api/tracks/:id */
  const saveSettingsToDb = useCallback(
    async (
      fields: Partial<{
        cameraProfileName: string;
        resolution: string;
        fps: number;
        customDuration: number;
        aspectRatio: string;
        mapProvider: string;
        hillshade: boolean;
        terrainExaggeration: number;
        introDuration: number;
        outroDuration: number;
        hudSettings: Record<string, boolean>;
      }>,
    ) => {
      if (!track || !token) return;
      const body = {
        cameraProfileName,
        resolution,
        fps,
        customDuration,
        aspectRatio,
        mapProvider,
        hillshade,
        terrainExaggeration,
        introDuration,
        outroDuration,
        hudSettings,
        ...fields,
      };
      try {
        await apiPatch(`/api/tracks/${track.id}`, body);
      } catch (err) {
        console.error('Error saving track settings to DB:', err);
      }
    },
    [
      track,
      cameraProfileName,
      resolution,
      fps,
      customDuration,
      aspectRatio,
      mapProvider,
      hillshade,
      terrainExaggeration,
      introDuration,
      outroDuration,
      hudSettings,
      token,
    ],
  );

  /** Save current settings as the global default for this activity type */
  const saveAsActivityDefault = async () => {
    if (!track) return;
    setSavingDefault(true);
    const body = {
      cameraProfileName,
      resolution,
      fps,
      customDuration,
      aspectRatio,
      mapProvider,
      hillshade,
      terrainExaggeration,
      introDuration,
      outroDuration,
      hudSettings,
    };
    try {
      await apiPost(`/api/tracks/settings/${track.activityType}`, body);
    } catch (err) {
      console.error('Error saving activity defaults to DB:', err);
    } finally {
      setSavingDefault(false);
    }
  };

  // 3.5 Bind window.renderFrameAtProgress for Puppeteer in Render Mode
  useEffect(() => {
    if (!isRenderMode || !mapRef.current || !animatorRef.current || !track) return;

    const startTimeMs = new Date(track.startTime).getTime();
    let lastRenderPhase: 'INTRO' | 'OUTRO' | 'ACTIVE' | null = null;

    (window as any).renderFrameAtProgress = async (progress: number) => {
      const map = mapRef.current;
      const animator = animatorRef.current;
      if (!map || !animator) return;

      const isIntro = progress < (animator as any).introRatio;
      const isOutro = progress > 1 - (animator as any).outroRatio;
      const phase: 'INTRO' | 'OUTRO' | 'ACTIVE' = isIntro ? 'INTRO' : isOutro ? 'OUTRO' : 'ACTIVE';

      const phaseChanged = lastRenderPhase !== phase;
      lastRenderPhase = phase;

      const effectiveProgress = (phase === 'INTRO' && phaseChanged) ? 0 : (phase === 'OUTRO' && phaseChanged) ? 1 : progress;
      const frame = animator.getKeyframeAtProgress(progress);

      // 1. Position camera FIRST so GeoJSON source setData calculations use the correct viewport
      safeJumpTo(map, frame, progress);

      // Synchronously update telemetry HUD elements in DOM to bypass React render delay
      const pt = animator.getTelemetryAtProgress(effectiveProgress);

      const distEl = document.getElementById('hud-distance');
      if (distEl) distEl.textContent = `${((pt.distance ?? 0) / 1000).toFixed(2)} km`;

      const elevEl = document.getElementById('hud-elevation');
      if (elevEl) elevEl.textContent = `${Math.round(pt.ele)} m`;

      const timeEl = document.getElementById('hud-duration');
      if (timeEl) {
        const currentMs = new Date(pt.time).getTime();
        const elapsedSeconds = Math.max(0, Math.floor((currentMs - startTimeMs) / 1000));

        const hrs = Math.floor(elapsedSeconds / 3600);
        const mins = Math.floor((elapsedSeconds % 3600) / 60);
        const secs = elapsedSeconds % 60;
        timeEl.textContent = [
          hrs.toString().padStart(2, '0'),
          mins.toString().padStart(2, '0'),
          secs.toString().padStart(2, '0'),
        ].join(':');
      }

      const paceEl = document.getElementById('hud-pace');
      if (paceEl) {
        const speed = pt.speed || 0;
        const isRunningOrHiking = ['running', 'hiking', 'climbing'].includes(track.activityType);
        if (isRunningOrHiking) {
          if (speed < 0.1) {
            paceEl.textContent = '-:-- /km';
          } else {
            const paceSec = 1000 / speed;
            const paceMinStr = Math.floor(paceSec / 60).toString();
            const paceSecStr = Math.floor(paceSec % 60)
              .toString()
              .padStart(2, '0');
            paceEl.textContent = `${paceMinStr}:${paceSecStr} /km`;
          }
        } else {
          const speedKmh = speed * 3.6;
          paceEl.textContent = `${speedKmh.toFixed(1)} km/h`;
        }
      }

      const hrEl = document.getElementById('hud-hr');
      if (hrEl) {
        hrEl.textContent = pt.hr ? `${pt.hr} bpm` : '';
      }

      // Only update cursor and track sources if phase is ACTIVE or if phase changed (e.g. entering INTRO/OUTRO)
      if (phase === 'ACTIVE' || phaseChanged) {
        // Update cursor marker
        const cursorSource = map.getSource('cursor') as maplibregl.GeoJSONSource;
        if (cursorSource) {
          cursorSource.setData({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [pt.lon, pt.lat],
            },
            properties: {},
          });
        }

        // Update track passed/remaining geometries
        const passedSource = map.getSource('track-passed') as maplibregl.GeoJSONSource;
        const remainingSource = map.getSource('track-remaining') as maplibregl.GeoJSONSource;
        if (passedSource && remainingSource && track?.telemetry?.points) {
          const targetDist = pt.distance || 0;
          const passedCoords: [number, number][] = [];
          const remainingCoords: [number, number][] = [];

          for (const p of track.telemetry.points) {
            if ((p.distance || 0) <= targetDist) {
              passedCoords.push([p.lon, p.lat]);
            } else {
              remainingCoords.push([p.lon, p.lat]);
            }
          }

          passedCoords.push([pt.lon, pt.lat]);
          remainingCoords.unshift([pt.lon, pt.lat]);

          passedSource.setData({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: passedCoords,
            },
            properties: {},
          });

          remainingSource.setData({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: remainingCoords,
            },
            properties: {},
          });
        }
      }

      // Update canvases
      const epCanvas = document.getElementById('hud-elevation-profile') as HTMLCanvasElement;
      const cmCanvas = document.getElementById('hud-course-miniature') as HTMLCanvasElement;
      if ((epCanvas || cmCanvas) && track?.telemetry?.points) {
        const totalDistance = track.telemetry.points[track.telemetry.points.length - 1].distance || 1;
        const syncedPct = (pt.distance || 0) / totalDistance;
        if (epCanvas) {
          drawElevationProfile(epCanvas, track.telemetry.points, syncedPct);
        }
        if (cmCanvas) {
          drawCourseMiniature(cmCanvas, track.telemetry.points, syncedPct);
        }
      }

      // Toggle HUD visibility for intro/outro if configured
      const hudEl = document.getElementById('hud-overlay-container');
      if (hudEl) {
        const isIntroOutro = progress === 0 || progress === 1 || progress < animator.introRatio || progress > 1 - animator.outroRatio;
        if (hudSettings.hideOverlayOnIntroOutro && isIntroOutro) {
          hudEl.style.display = 'none';
        } else {
          hudEl.style.display = 'flex';
        }
      }

      // 2-tier dynamic tile loading wait:
      // Initial frame (progress === 0 or first render): wait up to 15s for full DEM + hillshade loading.
      // Subsequent frames: if cached, areTilesLoaded() returns true in 0ms.
      const isInitialFrame = progress === 0 || !(window as any)._hasRenderedFirstFrame;
      const maxWaitMs = isInitialFrame ? 15000 : 5000;
      const tileWaitStart = Date.now();
      while (Date.now() - tileWaitStart < maxWaitMs) {
        if (map.loaded() && map.areTilesLoaded()) break;
        await new Promise((resolve) => setTimeout(resolve, 20));
      }
      const tileWaitMs = Date.now() - tileWaitStart;

      if (isInitialFrame) {
        (window as any)._hasRenderedFirstFrame = true;
      }

      // Trigger WebGL repaint and wait for 2 animation frames (requestAnimationFrame)
      // to guarantee WebGL texture uploads & GeoJSON buffer rendering complete on canvas
      const repaintStart = Date.now();
      map.triggerRepaint();
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            resolve();
          });
        });
      });
      const repaintWaitMs = Date.now() - repaintStart;

      if (isIntro || isOutro || progress < 0.1) {
        console.log(`[RenderDebug][${phase}] progress: ${progress.toFixed(4)} | tileWait: ${tileWaitMs}ms, repaintWait: ${repaintWaitMs}ms | Marker pt: [${pt.lon.toFixed(5)}, ${pt.lat.toFixed(5)}] | Map center: [${map.getCenter().lng.toFixed(5)}, ${map.getCenter().lat.toFixed(5)}], zoom=${map.getZoom().toFixed(2)}, pitch=${map.getPitch().toFixed(1)}`);
      }

      return { tileWaitMs, repaintWaitMs };
    };

    (window as any).mapReady = true;

    return () => {
      delete (window as any).renderFrameAtProgress;
      delete (window as any).mapReady;
    };
  }, [isRenderMode, track, isMapLoaded, safeJumpTo]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setProgress(val);
    updateFrame(val);
  };

  const handleTogglePlay = () => {
    setIsPlaying((prev) => {
      const nextPlay = !prev;
      if (nextPlay && progress >= 0.999) {
        setProgress(0);
        updateFrame(0);
      }
      return nextPlay;
    });
  };

  const handleReset = () => {
    setIsPlaying(false);
    setProgress(0);
    updateFrame(0);
  };

  const triggerRender = async () => {
    if (!id) return;
    setRenderingQueue(true);

    try {
      const { jobId } = await apiPost(`/api/tracks/${id}/render`, {
        resolution,
        fps,
        videoDuration: customDuration,
        aspectRatio,
        cameraAngle: PROFILES[cameraProfileName]?.pitch || 60,
      });

      trackGAEvent('start_video_render', {
        plan: user?.plan || 'free',
        resolution,
        fps,
        aspect_ratio: aspectRatio,
        track_length_km: track ? (track.distanceMeters / 1000) : 0,
      });

      navigate(`/status/${jobId}`);
    } catch (err: any) {
      ui.alert(err.message || 'Failed to request render', 'Render Request Failed');
    } finally {
      setRenderingQueue(false);
    }
  };

  const formatPace = (speedMps: number) => {
    return formatSpeedOrPace(speedMps, cameraProfileName, user?.units || 'metric');
  };

  if (loading) {
    return (
      <div className="flex-center" style={{ minHeight: '80vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (error || !track) {
    return (
      <div className="container" style={{ padding: '40px 24px', textAlign: 'center' }}>
        <div
          style={{
            padding: '24px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '12px',
            color: 'var(--status-error)',
            display: 'inline-block',
          }}
        >
          Error loading preview: {error || 'Track details missing'}
        </div>
        <div style={{ marginTop: '24px' }}>
          <button onClick={() => navigate('/library')} className="btn btn-secondary">
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  const { width: mapW, height: mapH } = getMapContainerDimensions();

  const minDim = Math.min(mapW, mapH);
  let hudScale = minDim / 1080;
  hudScale = Math.min(Math.max(hudScale, 0.38), 2.0);
  if (resolution === '720p') {
    hudScale = hudScale * 0.8;
  }

  return (
    <div style={{ display: 'flex', height: '100vh', maxHeight: '100vh', overflow: 'hidden', background: '#0b0f19' }}>
      {/* 3D Map Area */}
      <div
        ref={previewAreaCallbackRef}
        style={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#000',
          overflow: 'hidden',
          height: '100%',
        }}
      >
        <div
          ref={mapContainerRef}
          className="map-container-preview"
          style={
            isRenderMode
              ? { width: '100%', height: '100%', position: 'relative' }
              : {
                  width: `${mapW}px`,
                  height: `${mapH}px`,
                  position: 'relative',
                }
          }
        >
          {/* Floating Back Button */}
          {!isRenderMode && (
            <button
              onClick={() => navigate('/library')}
              className="btn btn-secondary"
              style={{
                position: 'absolute',
                top: '20px',
                left: '20px',
                padding: '10px 14px',
                borderRadius: '8px',
                zIndex: 10,
                background: 'rgba(11, 15, 25, 0.85)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <ArrowLeft size={18} />
              <span>Library</span>
            </button>
          )}

          {/* Floating Telemetry Stats HUD */}
          {hudSettings.showOverlay &&
            !(hudSettings.hideOverlayOnIntroOutro && (progress < (animatorRef.current?.introRatio || 0) || progress > 1 - (animatorRef.current?.outroRatio || 0) || progress === 0 || progress === 1)) && (
            <div
              id="hud-overlay-container"
              className="glass-panel"
              style={{
                position: 'absolute',
                bottom: `${30 * hudScale}px`,
                left: `${20 * hudScale}px`,
                padding: `${20 * hudScale}px`,
                width: `${320 * hudScale}px`,
                zIndex: 10,
                textAlign: 'left',
                background: 'rgba(11, 15, 25, 0.75)',
                borderColor: 'rgba(255,255,255,0.06)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                borderRadius: `${16 * hudScale}px`,
                display: 'flex',
                flexDirection: 'column',
                gap: `${16 * hudScale}px`,
              }}
            >
              <div
                style={{
                  fontSize: `${Math.round(14 * hudScale)}px`,
                  color: 'var(--text-secondary)',
                  marginBottom: `${Math.round(4 * hudScale)}px`,
                  textTransform: 'capitalize',
                  fontWeight: 600,
                }}
              >
                {track.activityType} Route Stats
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: `${12 * hudScale}px` }}>
                {hudSettings.showDistance && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      paddingBottom: `${6 * hudScale}px`,
                    }}
                  >
                    <span style={{ fontSize: `${Math.round(13 * hudScale)}px`, color: 'var(--text-secondary)' }}>
                      Distance
                    </span>
                    <strong id="hud-distance" style={{ fontSize: `${Math.round(16 * hudScale)}px`, color: '#fff' }}>
                      {formatDistance(stats.distance * 1000, user?.units || 'metric')}
                    </strong>
                  </div>
                )}

                {hudSettings.showElevation && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      paddingBottom: `${6 * hudScale}px`,
                    }}
                  >
                    <span style={{ fontSize: `${Math.round(13 * hudScale)}px`, color: 'var(--text-secondary)' }}>
                      Elevation
                    </span>
                    <strong id="hud-elevation" style={{ fontSize: `${Math.round(16 * hudScale)}px`, color: '#fff' }}>
                      {formatElevation(stats.elevation, user?.units || 'metric')}
                    </strong>
                  </div>
                )}

                {hudSettings.showDuration && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      paddingBottom: `${6 * hudScale}px`,
                    }}
                  >
                    <span style={{ fontSize: `${Math.round(13 * hudScale)}px`, color: 'var(--text-secondary)' }}>
                      Duration
                    </span>
                    <strong id="hud-duration" style={{ fontSize: `${Math.round(16 * hudScale)}px`, color: '#fff' }}>
                      {stats.elapsedTime}
                    </strong>
                  </div>
                )}

                {hudSettings.showPace && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderBottom:
                        hudSettings.showHeartRate &&
                        track.telemetry.points.some((p: any) => p.hr !== undefined && p.hr > 0)
                          ? '1px solid rgba(255,255,255,0.05)'
                          : 'none',
                      paddingBottom: `${6 * hudScale}px`,
                    }}
                  >
                    <span style={{ fontSize: `${Math.round(13 * hudScale)}px`, color: 'var(--text-secondary)' }}>
                      {cameraProfileName === 'cycling' ? 'Speed' : 'Pace'}
                    </span>
                    <strong id="hud-pace" style={{ fontSize: `${Math.round(16 * hudScale)}px`, color: '#fff' }}>
                      {formatPace(stats.speed)}
                    </strong>
                  </div>
                )}

                {hudSettings.showHeartRate &&
                  track.telemetry.points.some((p: any) => p.hr !== undefined && p.hr > 0) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#ff385c' }}>
                      <span
                        style={{
                          fontSize: `${Math.round(13 * hudScale)}px`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: `${4 * hudScale}px`,
                        }}
                      >
                        <Heart size={Math.round(14 * hudScale)} fill="#ff385c" />
                        <span>Heart Rate</span>
                      </span>
                      <strong id="hud-hr" style={{ fontSize: `${Math.round(16 * hudScale)}px` }}>
                        {stats.heartRate} bpm
                      </strong>
                    </div>
                  )}

                {/* Visual Profile & Map Canvases */}
                {(hudSettings.showElevationProfile || hudSettings.showCourseMiniature) && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: `${16 * hudScale}px`,
                      marginTop: `${16 * hudScale}px`,
                      borderTop: '1px solid rgba(255,255,255,0.08)',
                      paddingTop: `${16 * hudScale}px`,
                    }}
                  >
                    {hudSettings.showElevationProfile && (
                      <div>
                        <span
                          style={{
                            fontSize: `${Math.round(11 * hudScale)}px`,
                            color: 'var(--text-secondary)',
                            display: 'block',
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            marginBottom: `${6 * hudScale}px`,
                            letterSpacing: '0.5px',
                          }}
                        >
                          Elevation Profile
                        </span>
                        <canvas
                          id="hud-elevation-profile"
                          width={Math.round(240 * hudScale)}
                          height={Math.round(60 * hudScale)}
                          style={{
                            width: '100%',
                            height: `${60 * hudScale}px`,
                            background: 'rgba(0,0,0,0.25)',
                            borderRadius: `${6 * hudScale}px`,
                            display: 'block',
                          }}
                        />
                      </div>
                    )}

                    {hudSettings.showCourseMiniature && (
                      <div>
                        <span
                          style={{
                            fontSize: `${Math.round(11 * hudScale)}px`,
                            color: 'var(--text-secondary)',
                            display: 'block',
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            marginBottom: `${6 * hudScale}px`,
                            letterSpacing: '0.5px',
                          }}
                        >
                          Course Map
                        </span>
                        <canvas
                          id="hud-course-miniature"
                          width={Math.round(240 * hudScale)}
                          height={Math.round(120 * hudScale)}
                          style={{
                            width: '100%',
                            height: `${120 * hudScale}px`,
                            background: 'rgba(0,0,0,0.25)',
                            borderRadius: `${6 * hudScale}px`,
                            display: 'block',
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Render Configuration Sidebar */}
      {!isRenderMode && (
        <div
          className="glass-panel"
          style={{
            width: '380px',
            height: '100%',
            borderLeft: '1px solid var(--border-glass)',
            borderTop: 'none',
            borderRight: 'none',
            borderBottom: 'none',
            borderRadius: 0,
            background: 'rgba(17, 24, 39, 0.8)',
            padding: '32px 24px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            zIndex: 20,
            overflowY: 'auto',
          }}
        >
          <div>
            <div style={{ marginBottom: '28px' }}>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  color: 'var(--accent-primary)',
                  textTransform: 'uppercase',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Interactive Preview
              </span>
              {isEditingName ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveName();
                      else if (e.key === 'Escape') setIsEditingName(false);
                    }}
                    onBlur={(e) => {
                      const target = e.relatedTarget as HTMLElement;
                      if (target && (target.innerText === 'Save' || target.innerText === 'Cancel')) {
                        return;
                      }
                      setTimeout(() => {
                        setIsEditingName(false);
                      }, 150);
                    }}
                    autoFocus
                    style={{
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '1px solid var(--accent-primary)',
                      borderRadius: '6px',
                      color: '#fff',
                      padding: '4px 8px',
                      fontSize: '15px',
                      fontWeight: 600,
                      width: '100%',
                      outline: 'none',
                    }}
                  />
                  <button
                    onClick={saveName}
                    className="btn btn-primary"
                    style={{ padding: '4px 8px', fontSize: '11px', borderRadius: '4px' }}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setIsEditingName(false)}
                    className="btn btn-secondary"
                    style={{ padding: '4px 8px', fontSize: '11px', borderRadius: '4px' }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <h2
                    style={{
                      fontSize: '22px',
                      color: '#fff',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      margin: 0,
                      cursor: 'pointer',
                      flex: 1,
                    }}
                    onClick={startEditing}
                    title="Click to rename activity"
                  >
                    {track.name}
                  </h2>
                  <button
                    onClick={startEditing}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      padding: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      opacity: 0.6,
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
                    title="Rename activity"
                  >
                    <Edit2 size={14} />
                  </button>
                </div>
              )}
            </div>

            {/* Camera Playback Controls */}
            <div
              className="glass-panel"
              style={{ padding: '16px', marginBottom: '32px', background: 'rgba(0,0,0,0.2)' }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}
              >
                <button onClick={handleReset} className="btn btn-secondary" style={{ padding: '8px' }}>
                  <RotateCcw size={16} />
                </button>

                <button
                  onClick={handleTogglePlay}
                  className="btn btn-primary"
                  style={{ padding: '12px 20px', borderRadius: '30px' }}
                >
                  {isPlaying ? <Pause size={18} fill="#fff" /> : <Play size={18} fill="#fff" />}
                  <span>{isPlaying ? 'Pause' : 'Play'}</span>
                </button>

                <select
                  className="text-input"
                  style={{ width: '80px', padding: '6px', fontSize: '13px' }}
                  value={playbackSpeed}
                  onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
                >
                  <option value={0.5}>0.5x</option>
                  <option value={1}>1.0x</option>
                  <option value={2}>2.0x</option>
                  <option value={4}>4.0x</option>
                </select>
              </div>

              {/* Slider */}
              <div>
                <input
                  type="range"
                  min={0}
                  max={0.999}
                  step={0.001}
                  value={progress}
                  onChange={handleSliderChange}
                  style={{
                    width: '100%',
                    accentColor: 'var(--accent-primary)',
                    cursor: 'pointer',
                    height: '4px',
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '11px',
                    color: 'var(--text-secondary)',
                    marginTop: '4px',
                  }}
                >
                  <span>Start</span>
                  <span>{Math.round(progress * 100)}%</span>
                  <span>End</span>
                </div>
              </div>
            </div>

            {/* Configuration Form */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Group 1: Video Format & Timeline */}
              <div
                style={{
                  background: 'rgba(0,0,0,0.15)',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.03)',
                }}
              >
                <button
                  onClick={() => toggleSection('timeline')}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    background: 'rgba(255,255,255,0.02)',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#fff',
                    outline: 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 700 }}>
                    <Video size={15} style={{ color: 'var(--accent-primary)' }} />
                    <span>Format & Timeline</span>
                  </div>
                  <ChevronDown
                    size={15}
                    style={{
                      transform: expandedSections.timeline ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                      color: 'var(--text-secondary)',
                    }}
                  />
                </button>

                {expandedSections.timeline && (
                  <div
                    style={{
                      padding: '14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '14px',
                      background: 'rgba(0,0,0,0.1)',
                    }}
                  >
                    <div className="input-group">
                      <label className="input-label">Camera Movement Profile</label>
                      <select
                        className="text-input"
                        value={cameraProfileName}
                        onChange={(e) => {
                          const val = e.target.value;
                          setCameraProfileName(val);
                          const prof = PROFILES[val] || PROFILES.other;
                          setTerrainExaggeration(prof.terrainExaggeration);
                          saveSettingsToDb({ cameraProfileName: val, terrainExaggeration: prof.terrainExaggeration });
                        }}
                      >
                        <option value="running">Running Profile 🏃</option>
                        <option value="cycling">Cycling Profile 🚴</option>
                        <option value="hiking">Hiking Profile 🥾</option>
                        <option value="climbing">Climbing Profile 🧗</option>
                        <option value="other">Generic Profile 📦</option>
                      </select>
                    </div>

                    <div className="input-group">
                      <label className="input-label">Aspect Ratio Preset</label>
                      <select
                        className="text-input"
                        value={aspectRatio}
                        onChange={(e) => {
                          const val = e.target.value as any;
                          setAspectRatio(val);
                          saveSettingsToDb({ aspectRatio: val });
                        }}
                      >
                        <option value="16:9">16:9 (Landscape) 📺</option>
                        <option value="4:3">4:3 (Traditional) 📷</option>
                        <option value="1:1">1:1 (Square) 🟩</option>
                        <option value="9:16">9:16 (Vertical/Reels) 📱</option>
                      </select>
                    </div>

                    <div className="input-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <label className="input-label">Video Duration</label>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--accent-secondary)' }}>
                          {customDuration} seconds
                        </span>
                      </div>
                      <input
                        type="range"
                        min={5}
                        max={300}
                        step={5}
                        value={customDuration}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          setCustomDuration(val);
                          saveSettingsToDb({ customDuration: val });
                        }}
                        style={{ width: '100%', accentColor: 'var(--accent-primary)', cursor: 'pointer' }}
                      />
                      {customDuration !== suggestedDuration && (
                        <div
                          style={{
                            fontSize: '12px',
                            color: 'var(--accent-primary)',
                            cursor: 'pointer',
                            marginTop: '4px',
                            opacity: 0.85,
                          }}
                          onClick={() => {
                            setCustomDuration(suggestedDuration);
                            saveSettingsToDb({ customDuration: suggestedDuration });
                          }}
                        >
                          💡 Suggested: {suggestedDuration}s
                        </div>
                      )}
                    </div>

                    <div className="input-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <label className="input-label">Intro Duration (Zoom-in)</label>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--accent-secondary)' }}>
                          {introDuration} seconds
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={10}
                        step={0.5}
                        value={introDuration}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          setIntroDuration(val);
                          saveSettingsToDb({ introDuration: val });
                        }}
                        style={{ width: '100%', accentColor: 'var(--accent-primary)', cursor: 'pointer' }}
                      />
                    </div>

                    <div className="input-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <label className="input-label">Outro Duration (Zoom-out)</label>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--accent-secondary)' }}>
                          {outroDuration} seconds
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={10}
                        step={0.5}
                        value={outroDuration}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          setOutroDuration(val);
                          saveSettingsToDb({ outroDuration: val });
                        }}
                        style={{ width: '100%', accentColor: 'var(--accent-primary)', cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Group 2: Map Appearance */}
              <div
                style={{
                  background: 'rgba(0,0,0,0.15)',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.03)',
                }}
              >
                <button
                  onClick={() => toggleSection('map')}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    background: 'rgba(255,255,255,0.02)',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#fff',
                    outline: 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 700 }}>
                    <Layers size={15} style={{ color: 'var(--accent-primary)' }} />
                    <span>Map Appearance</span>
                  </div>
                  <ChevronDown
                    size={15}
                    style={{
                      transform: expandedSections.map ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                      color: 'var(--text-secondary)',
                    }}
                  />
                </button>

                {expandedSections.map && (
                  <div
                    style={{
                      padding: '14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '14px',
                      background: 'rgba(0,0,0,0.1)',
                    }}
                  >
                    <div className="input-group">
                      <label className="input-label">Map Provider</label>
                      <select
                        className="text-input"
                        value={mapProvider}
                        onChange={(e) => {
                          const val = e.target.value;
                          setMapProvider(val);
                          saveSettingsToDb({ mapProvider: val });
                        }}
                      >
                        {tileConfig?.providers.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <label
                        style={{
                          fontSize: '14px',
                          color: 'var(--text-primary)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                        htmlFor="hillshade-toggle"
                      >
                        Hillshade (terrain shadows)
                      </label>
                      <label
                        style={{
                          position: 'relative',
                          display: 'inline-block',
                          width: '44px',
                          height: '24px',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          id="hillshade-toggle"
                          type="checkbox"
                          checked={hillshade}
                          onChange={(e) => {
                            const val = e.target.checked;
                            setHillshade(val);
                            saveSettingsToDb({ hillshade: val });
                          }}
                          style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: hillshade ? 'var(--accent-primary)' : 'rgba(255,255,255,0.1)',
                            borderRadius: '24px',
                            transition: 'background 0.2s',
                          }}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            top: '2px',
                            left: hillshade ? '22px' : '2px',
                            width: '20px',
                            height: '20px',
                            background: '#fff',
                            borderRadius: '50%',
                            transition: 'left 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                          }}
                        />
                      </label>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        background: 'rgba(255,255,255,0.02)',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.04)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                          Terrain Exaggeration
                        </label>
                        <strong style={{ fontSize: '13px', color: 'var(--accent-primary)' }}>
                          {terrainExaggeration.toFixed(1)}x
                        </strong>
                      </div>
                      <input
                        type="range"
                        min="0.1"
                        max="5.0"
                        step="0.1"
                        value={terrainExaggeration}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          setTerrainExaggeration(val);
                          saveSettingsToDb({ terrainExaggeration: val });
                        }}
                        style={{ width: '100%', accentColor: 'var(--accent-primary)', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                        Adjust elevation scale. Higher = steeper mountains.
                      </span>
                    </div>

                    <button
                      onClick={saveAsActivityDefault}
                      disabled={savingDefault}
                      className="btn btn-secondary"
                      style={{ width: '100%', padding: '9px', fontSize: '12px', opacity: savingDefault ? 0.6 : 1 }}
                    >
                      <Save size={13} />
                      <span>
                        {savingDefault ? 'Saving…' : `Set as default for all ${track.activityType} activities`}
                      </span>
                    </button>
                  </div>
                )}
              </div>

              {/* Group 3: HUD Overlay */}
              <div
                style={{
                  background: 'rgba(0,0,0,0.15)',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.03)',
                }}
              >
                <button
                  onClick={() => toggleSection('hud')}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    background: 'rgba(255,255,255,0.02)',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#fff',
                    outline: 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 700 }}>
                    <Eye size={15} style={{ color: 'var(--accent-primary)' }} />
                    <span>HUD Stats Overlay</span>
                  </div>
                  <ChevronDown
                    size={15}
                    style={{
                      transform: expandedSections.hud ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                      color: 'var(--text-secondary)',
                    }}
                  />
                </button>

                {expandedSections.hud && (
                  <div
                    style={{
                      padding: '14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '14px',
                      background: 'rgba(0,0,0,0.1)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <label
                        style={{
                          fontSize: '14px',
                          color: 'var(--text-primary)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                        htmlFor="hud-overlay-toggle"
                      >
                        Show Stats HUD
                      </label>
                      <label
                        style={{
                          position: 'relative',
                          display: 'inline-block',
                          width: '44px',
                          height: '24px',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          id="hud-overlay-toggle"
                          type="checkbox"
                          checked={hudSettings.showOverlay}
                          onChange={(e) => {
                            const val = e.target.checked;
                            const updated = { ...hudSettings, showOverlay: val };
                            setHudSettings(updated);
                            saveSettingsToDb({ hudSettings: updated });
                          }}
                          style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: hudSettings.showOverlay ? 'var(--accent-primary)' : 'rgba(255,255,255,0.1)',
                            borderRadius: '24px',
                            transition: 'background 0.2s',
                          }}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            top: '2px',
                            left: hudSettings.showOverlay ? '22px' : '2px',
                            width: '20px',
                            height: '20px',
                            background: '#fff',
                            borderRadius: '50%',
                            transition: 'left 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                          }}
                        />
                      </label>
                    </div>

                    {hudSettings.showOverlay && (
                      <div
                        style={{
                          paddingLeft: '8px',
                          borderLeft: '2px solid rgba(255,255,255,0.06)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                        }}
                      >
                        {[
                          { key: 'showDistance', label: 'Distance' },
                          { key: 'showElevation', label: 'Elevation' },
                          { key: 'showDuration', label: 'Duration' },
                          { key: 'showPace', label: cameraProfileName === 'cycling' ? 'Speed' : 'Pace' },
                          {
                            key: 'showHeartRate',
                            label: 'Heart Rate',
                            disabled: !track.telemetry.points.some((p: any) => p.hr !== undefined && p.hr > 0),
                          },
                          { key: 'showElevationProfile', label: 'Elevation Profile Graph' },
                          { key: 'showCourseMiniature', label: 'Course Map Miniature' },
                          { key: 'hideOverlayOnIntroOutro', label: 'Hide Stats on Intro & Outro (Clean Frames)' },
                        ].map((item) => {
                          if (item.disabled) return null;
                          const val = hudSettings[item.key];
                          return (
                            <div
                              key={item.key}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                            >
                              <label
                                style={{ fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer' }}
                                htmlFor={`hud-toggle-${item.key}`}
                              >
                                {item.label}
                              </label>
                              <label
                                style={{
                                  position: 'relative',
                                  display: 'inline-block',
                                  width: '36px',
                                  height: '20px',
                                  cursor: 'pointer',
                                }}
                              >
                                <input
                                  id={`hud-toggle-${item.key}`}
                                  type="checkbox"
                                  checked={val}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    const updated = { ...hudSettings, [item.key]: checked };
                                    setHudSettings(updated);
                                    saveSettingsToDb({ hudSettings: updated });
                                  }}
                                  style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                                />
                                <span
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: val ? 'var(--accent-primary)' : 'rgba(255,255,255,0.1)',
                                    borderRadius: '20px',
                                    transition: 'background 0.2s',
                                  }}
                                />
                                <span
                                  style={{
                                    position: 'absolute',
                                    top: '2px',
                                    left: val ? '18px' : '2px',
                                    width: '16px',
                                    height: '16px',
                                    background: '#fff',
                                    borderRadius: '50%',
                                    transition: 'left 0.2s',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.4)',
                                  }}
                                />
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Group 4: Render Quality */}
              <div
                style={{
                  background: 'rgba(0,0,0,0.15)',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.03)',
                }}
              >
                <button
                  onClick={() => toggleSection('quality')}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    background: 'rgba(255,255,255,0.02)',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#fff',
                    outline: 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 700 }}>
                    <Settings size={15} style={{ color: 'var(--accent-primary)' }} />
                    <span>Render Quality</span>
                  </div>
                  <ChevronDown
                    size={15}
                    style={{
                      transform: expandedSections.quality ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                      color: 'var(--text-secondary)',
                    }}
                  />
                </button>

                {expandedSections.quality && (
                  <div
                    style={{
                      padding: '14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '14px',
                      background: 'rgba(0,0,0,0.1)',
                    }}
                  >
                    <div className="input-group">
                      <label className="input-label">Output Resolution</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                        {(['720p', '1080p', '4K'] as const).map((res) => {
                          const isLocked = limits[res] === 0;
                          return (
                            <button
                              key={res}
                              disabled={isLocked}
                              onClick={() => {
                                setResolution(res);
                                saveSettingsToDb({ resolution: res });
                              }}
                              className="btn"
                              style={{
                                padding: '10px 0',
                                fontSize: '13px',
                                background: resolution === res ? 'var(--accent-gradient)' : 'rgba(255,255,255,0.03)',
                                border: '1px solid',
                                borderColor: resolution === res ? 'transparent' : 'var(--border-glass)',
                                color: isLocked ? 'var(--text-muted)' : '#fff',
                                cursor: isLocked ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                opacity: isLocked ? 0.5 : 1,
                              }}
                            >
                              {isLocked && <Lock size={12} />}
                              <span>{res}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="input-group">
                      <label className="input-label">Frame Rate (FPS)</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                        {([30, 60] as const).map((rate) => {
                          const isLocked = rate === 60 && !['pro', 'expert', 'ultra'].includes(userPlan);
                          return (
                            <button
                              key={rate}
                              disabled={isLocked}
                              onClick={() => {
                                setFps(rate);
                                saveSettingsToDb({ fps: rate });
                              }}
                              className="btn"
                              style={{
                                padding: '10px 0',
                                fontSize: '13px',
                                background: fps === rate ? 'var(--accent-gradient)' : 'rgba(255,255,255,0.03)',
                                border: '1px solid',
                                borderColor: fps === rate ? 'transparent' : 'var(--border-glass)',
                                color: isLocked ? 'var(--text-muted)' : '#fff',
                                cursor: isLocked ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                opacity: isLocked ? 0.5 : 1,
                              }}
                            >
                              {isLocked && <Lock size={12} />}
                              <span>{rate} FPS</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Generate Video Trigger */}
          <div style={{ borderTop: '1px solid var(--border-glass)', paddingTop: '24px', marginTop: '24px' }}>
            <button
              onClick={triggerRender}
              className="btn btn-primary"
              style={{ width: '100%', padding: '16px' }}
              disabled={renderingQueue}
            >
              {renderingQueue ? (
                <div className="spinner" style={{ width: '20px', height: '20px' }} />
              ) : (
                <>
                  <Video size={18} />
                  <span>Generate Video</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Canvas drawing helpers for HUD overlay
function drawElevationProfile(canvas: HTMLCanvasElement, points: any[], pct: number) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);

  if (points.length < 2) return;

  const elevations = points.map((p: any) => p.ele);
  const minEle = Math.min(...elevations);
  const maxEle = Math.max(...elevations);
  const eleRange = maxEle - minEle || 1;
  const totalDistance = points[points.length - 1].distance || 1;

  // Draw gradient background under elevation profile
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, 'rgba(255, 56, 92, 0.15)');
  grad.addColorStop(1, 'rgba(255, 56, 92, 0.0)');

  // Build path
  ctx.beginPath();
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const x = ((p.distance || 0) / totalDistance) * w;
    const y = h - ((p.ele - minEle) / eleRange) * (h - 12) - 6;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }

  // Fill gradient
  ctx.lineTo(w, h);
  ctx.lineTo(0, h);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();

  // Draw stroke line
  ctx.beginPath();
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const x = ((p.distance || 0) / totalDistance) * w;
    const y = h - ((p.ele - minEle) / eleRange) * (h - 12) - 6;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Draw passed portion in bright color
  const targetDist = pct * totalDistance;
  ctx.beginPath();
  let currentY = h / 2;
  let currentX = 0;
  let hasPassed = false;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const x = ((p.distance || 0) / totalDistance) * w;
    const y = h - ((p.ele - minEle) / eleRange) * (h - 12) - 6;
    if ((p.distance || 0) <= targetDist) {
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
      currentX = x;
      currentY = y;
      hasPassed = true;
    }
  }
  if (hasPassed) {
    ctx.strokeStyle = '#ff385c';
    ctx.lineWidth = 2.5;
    ctx.stroke();

    // Draw current location dot
    ctx.beginPath();
    ctx.arc(currentX, currentY, 4.5, 0, 2 * Math.PI);
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#ff385c';
    ctx.lineWidth = 2;
    ctx.fill();
    ctx.stroke();
  }
}

function drawCourseMiniature(canvas: HTMLCanvasElement, points: any[], pct: number) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);

  if (points.length < 2) return;

  let minLat = Infinity,
    maxLat = -Infinity,
    minLon = Infinity,
    maxLon = -Infinity;
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lon < minLon) minLon = p.lon;
    if (p.lon > maxLon) maxLon = p.lon;
  }

  const dLon = maxLon - minLon || 0.0001;
  const dLat = maxLat - minLat || 0.0001;
  const avgLat = (minLat + maxLat) / 2;
  const latCos = Math.cos((avgLat * Math.PI) / 180);

  const rangeX = dLon * latCos;
  const rangeY = dLat;

  const padding = 10;
  const plotW = w - 2 * padding;
  const plotH = h - 2 * padding;

  const scale = Math.min(plotW / rangeX, plotH / rangeY);

  const offsetX = padding + (plotW - rangeX * scale) / 2;
  const offsetY = padding + (plotH - rangeY * scale) / 2;

  const getCanvasCoords = (lon: number, lat: number) => {
    const x = offsetX + (lon - minLon) * latCos * scale;
    const y = offsetY + (maxLat - lat) * scale;
    return { x, y };
  };

  // 1. Draw entire track shape in semi-transparent white
  ctx.beginPath();
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const { x, y } = getCanvasCoords(p.lon, p.lat);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();

  // 2. Draw passed track shape in thick red
  const totalDistance = points[points.length - 1].distance || 1;
  const targetDist = pct * totalDistance;

  ctx.beginPath();
  let currentX = 0;
  let currentY = 0;
  let hasPassed = false;

  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const { x, y } = getCanvasCoords(p.lon, p.lat);
    if ((p.distance || 0) <= targetDist) {
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
      currentX = x;
      currentY = y;
      hasPassed = true;
    }
  }

  if (hasPassed) {
    ctx.strokeStyle = '#ff385c';
    ctx.lineWidth = 4;
    ctx.stroke();

    // 3. Draw dot at current position
    ctx.beginPath();
    ctx.arc(currentX, currentY, 5, 0, 2 * Math.PI);
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#ff385c';
    ctx.lineWidth = 2;
    ctx.fill();
    ctx.stroke();
  }
}
